/* eslint-disable no-shadow */
import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { Scoped } from "kremling"
import * as actions from "../../services/actions"
import { getLevelId } from "../components/utils"

// CSS
import styles from "../AdminDashboard.krem.css"

// Components
import CardHeader from "../components/CardHeader/CardHeader"
import Summary from "../Summary/Summary"
import AnalyzeRisks from "../AnalyzeRisks/AnalyzeRisks"
import AdvancedScoreChart from "../components/AdvancedScoreChart/AdvancedScoreChart"
import OrgPath from "../components/OrgPath/OrgPath"
import { ScopedLoading } from "../components/Loading/Loading"

// Selectors
import {
  getBehaviorTypes,
  getHierarchicalScoresId,
  getOverallScoreId,
  getScoresDistributionsId,
  getRiskDistributionsId,
  getBehaviorMap,
  getAllScoresId,
  getBehaviorMapId,
  getDepartment,
  getCurrentId,
  getFullPathBreadcrumbs,
  getShortPathBreadcrumbs,
  getTeamLoaded
} from "../../services/selectors/visionSelectors"

class Team extends Component {
  componentDidMount() {
    const { actions, history, teamBehaviorMap } = this.props
    const { level, id } = getLevelId(history)
    actions.setPage(level, id)
    actions.resetColumn()
    actions.resetTab()

    // Check if data already exists
    if (Object.keys(teamBehaviorMap).length < 2) {
      actions.resetLoaded()
      actions.getSubBehaviorMap({
        level,
        levelId: id
      })

      actions.getBreadcrumbs({
        levelId: id
      })

      actions.getBehaviorCategories({})

      actions.getHistoricalTrend({
        level,
        levelId: id
      })

      actions.getScoresDistributions({
        level,
        levelId: id
      })

      actions.getRiskDistributions({
        level,
        levelId: id
      })
    } else {
      actions.getBreadcrumbs({
        levelId: id
      })
      actions.setLoaded()
      actions.sortColumn("name")
    }
  }

  render() {
    const {
      id,
      keys,
      scoresDistributions,
      monthlyScore,
      topOverallScore,
      riskDistributions,
      scoreDetails,
      teamBehaviorMap,
      department,
      fullPathBreadcrumbs,
      shortPathBreadcrumbs,
      loadedCount
    } = this.props

    let fullPath = [
      {
        id: "Vision",
        name: "Vision",
        level: "Vision",
        directHome: true
      },
      {
        id: "Company",
        name: "All",
        level: "Company",
        directHome: true
      }
    ]

    fullPath = [...fullPath, ...fullPathBreadcrumbs]

    const parent_id =
      fullPathBreadcrumbs && fullPathBreadcrumbs[0] && fullPathBreadcrumbs[0].id
        ? fullPathBreadcrumbs[0].id
        : id

    let shortPath = [
      {
        id: parent_id,
        name: department,
        level: "department",
        directHome: false
      }
    ]

    shortPath = [...shortPath, ...shortPathBreadcrumbs]

    const summaryProps = {
      topOverallScore,
      riskDistributions,
      level: "Team",
      detail: true,
      scoreDetails
    }

    const behaviorMapTypes = ["name"].concat(keys)

    const advancedScoreChartProps = {
      children: teamBehaviorMap,
      childLevel: "team",
      links: true,
      title: "Teams",
      level: "team",
      keys: behaviorMapTypes,
      childUrl: "/team/",
      className: "card",

      getScores: (key, team) => {
        if (Object.keys(team).length) {
          const score = team.scores.filter(obj => {
            return obj.key === key
          })
          if (key === "name") {
            return team.name
          }
          if (score[0]) {
            return score[0].scores.score
          }
        }
        return null
      }
    }

    const analyzeRisksProps = {
      scoresDistributions,
      monthlyScore,
      behaviorTypes: keys,
      callData: false
    }

    if (!loadedCount) return <ScopedLoading />

    return (
      <Scoped postcss={styles}>
        <div>
          {/* Company Name and Summary */}
          <div className="card">
            <OrgPath path={fullPath} size="small" />
            <OrgPath path={shortPath} size="large" />
            <div className="card-block mb-0 p-0">
              <Summary {...summaryProps} />
            </div>
          </div>

          {/* Departmental Behavior Map */}
          <AdvancedScoreChart {...advancedScoreChartProps} />

          {/* Analyze Risks Tabs */}
          <div className="card">
            <CardHeader
              header={{ title: "Analyze Risks" }}
              noBottomBorder="border-bottom-0 mt-3"
              titleSize="h1"
              className="card"
            />
            <div className="card-block mb-0 p-0">
              <AnalyzeRisks {...analyzeRisksProps} />
            </div>
          </div>
        </div>
      </Scoped>
    )
  }
}

Team.propTypes = {
  actions: PropTypes.shape({
    setPage: PropTypes.func,
    resetColumn: PropTypes.func,
    resetTab: PropTypes.func,
    getSubBehaviorMap: PropTypes.func,
    getBreadcrumbs: PropTypes.func,
    getTopBottomData: PropTypes.func,
    getBehaviorCategories: PropTypes.func,
    getHistoricalTrend: PropTypes.func,
    getScoresDistributions: PropTypes.func,
    getRiskDistributions: PropTypes.func,
    resetLoaded: PropTypes.func,
    setLoaded: PropTypes.func,
    sortColumn: PropTypes.func
  }).isRequired,
  history: PropTypes.shape({}).isRequired,
  id: PropTypes.string.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  scoresDistributions: PropTypes.shape({}).isRequired,
  monthlyScore: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  topOverallScore: PropTypes.number,
  riskDistributions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  scoreDetails: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  teamBehaviorMap: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  department: PropTypes.string,
  fullPathBreadcrumbs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  shortPathBreadcrumbs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loadedCount: PropTypes.bool.isRequired
}

Team.defaultProps = {
  department: "",
  topOverallScore: null
}

function mapStateToProps(state, ownProps) {
  return {
    keys: getBehaviorTypes(state),
    monthlyScore: getHierarchicalScoresId(state, ownProps),
    topOverallScore: getOverallScoreId(state, ownProps),
    scoresDistributions: getScoresDistributionsId(state, ownProps),
    riskDistributions: getRiskDistributionsId(state, ownProps),
    behaviorMapData: getBehaviorMap(state),
    scoreDetails: getAllScoresId(state, ownProps),
    teamBehaviorMap: getBehaviorMapId(state, ownProps),
    department: getDepartment(state),
    id: getCurrentId(state, ownProps),
    fullPathBreadcrumbs: getFullPathBreadcrumbs(state, ownProps),
    shortPathBreadcrumbs: getShortPathBreadcrumbs(state, ownProps),
    loadedCount: getTeamLoaded(state)
  }
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    actions: bindActionCreators(actions, dispatch)
  }
}

// export Team
export default connect(mapStateToProps, mapDispatchToProps)(Team)
